import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';

import type { Message, Role } from '@/api/types';
import { tr, useT, type MessageKey } from '@/i18n';
import { noticeOf, type Notice } from '@/lib/errors';
import { formatBytes, MAX_FILE_BYTES, MAX_TEXT_BYTES } from '@/lib/format';
import { inTurn, sealFile, sealText } from '@/services/outgoing';

import shared from '@/pages/pages.module.css';

import styles from './chat.module.css';
import { ChatCopiedNotice } from './ChatCopiedNotice';
import { ChatMessage, sideName, type ChatSide } from './ChatMessage';
import { BackButton } from './BackButton';
import { ErrorNote } from './ErrorNote';
import { ArrowUpIcon, CloseIcon, EyeIcon, EyeOffIcon, FileIcon, LockIcon, PaperclipIcon, TrashIcon } from './Icons';
import { CHAT_JOINED, SharePopover } from './SharePopover';
import { percent } from './useFileDownload';
import type { ChatViewProps } from './ChatView.types';

/** What the guest's head says under the title; the locked chat of the shared link (OpenGate) says the same, so it wraps the same. */
export const GUEST_TOPIC: MessageKey = 'chatView.guestTopic';

const encoder = new TextEncoder();
/** Messages from the same side within this time read as one block, like in Slack. */
const GROUP_MS = 5 * 60 * 1000;

function sideOf(from: Role, viewer: Role, purpose: 'chat' | 'receive'): ChatSide {
  if (from === viewer) return 'me';
  if (viewer === 'owner') return purpose === 'receive' ? 'device' : 'guest';
  return 'host';
}

function groupedWith(previous: Message | undefined, message: Message): boolean {
  return previous?.from === message.from && new Date(message.createdAt).getTime() - new Date(previous.createdAt).getTime() < GROUP_MS;
}

/** Enter sends where there is a real keyboard; on touch screens Enter is a new line and the button sends. */
function enterSends(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * The two-way bridge, laid out like a channel in Slack and the Passwork
 * messenger: a header with the name and the bridge's state, the messages
 * with avatars, and one composer box with attach and send inside it.
 */
export function ChatView({ view, bridgeKey, purpose, online, actions, share, onDelete, onRemoveMessage, backTo = '/', presence = null, notice = null, copied = false }: ChatViewProps): React.JSX.Element {
  const t = useT();
  // Every message is shown; one switch in the head masks them all at once (since 0.22.4)
  const [hideAll, setHideAll] = useState(false);
  // the plate that says the link is on the clipboard stays until the other device joins or it is closed
  const [closed, setClosed] = useState(false);
  const isOwner = view.role === 'owner';
  const waiting = copied && view.views === 0;
  const showCopied = waiting && !closed;
  const writer = useMemo(() => ({ bridgeId: view.id, from: view.role }), [view.id, view.role]);
  const [draft, setDraft] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<Notice | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const messageCount = view.messages.length;

  useEffect(() => {
    const el = list.current;
    // an empty chat shows its top: the plate that says the link is copied is not scrolled out of sight
    if (messageCount > 0 && el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
  }, [messageCount]);

  // The box grows with the text, up to about eight lines.
  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${String(Math.min(el.scrollHeight, 200))}px`;
  }, [draft]);

  const draftBytes = encoder.encode(draft).length;
  const error =
    draftBytes > MAX_TEXT_BYTES
      ? t('chatView.textTooLarge')
      : file !== null && file.size > MAX_FILE_BYTES
        ? t('chatView.fileTooLarge')
        : null;
  const canSend = error === null && (draft.trim().length > 0 || file !== null) && !sending;

  const send = useCallback(async () => {
    if (!canSend) return;
    setSending(true);
    setSendError(null);
    try {
      // Text and a file can go together: the words first, then the file, as two messages.
      if (draft.trim().length > 0) {
        await inTurn(writer.bridgeId, async () => {
          await actions.send(await sealText(bridgeKey, draft, writer));
        });
        setDraft('');
      }
      if (file) {
        await inTurn(writer.bridgeId, async () => {
          await actions.sendFile(await sealFile(bridgeKey, file, writer), setProgress);
        });
        setFile(null);
      }
      field.current?.focus({ preventScroll: true });
    } catch (cause) {
      // the banner under the field is the one place the error shows, so no notice on top of it
      setSendError(noticeOf(cause, t('chatView.sendFailed'), t('common.send')));
    } finally {
      setSending(false);
      setProgress(null);
    }
  }, [canSend, file, actions, bridgeKey, draft, writer, t]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    if (event.metaKey || event.ctrlKey || (!event.shiftKey && enterSends())) {
      event.preventDefault();
      void send();
    }
  };

  const pickFile = (event: ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  // on a phone the title breaks after "opened": the second line is "a secure chat with you"
  const title: React.ReactNode = isOwner ? (
    purpose === 'receive' ? t('chatView.titleReceive') : t('chatView.titleChat')
  ) : (
    tr('chatView.titleGuest', { break: () => <br className={shared.phoneBreak} /> })
  );
  const topic = isOwner
    ? purpose === 'receive'
      ? t('chatView.topicReceive')
      : t('chatView.topicChat')
    : t(GUEST_TOPIC);
  const otherSide = sideOf(isOwner ? 'guest' : 'owner', view.role, purpose);
  const placeholder = file ? t('chatView.placeholderFile') : otherSide === 'device' ? t('chatView.placeholderDevice') : t('chatView.placeholderPerson', { name: sideName(otherSide) });

  return (
    <div className={styles.chat}>
      <header className={styles.head}>
        <span className={styles.headBack}>
          <BackButton to={backTo} label={backTo === '/' ? t('chatView.backStart') : t('chatView.backBridge')} />
        </span>
        <div className={styles.headText}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.topic}>{topic}</p>
        </div>
        <div className={styles.headActions}>
          {/* one switch for the whole conversation: every message shown, or every message masked (each can still be shown on its own) */}
          <button
            type="button"
            className={styles.headButton}
            onClick={() => {
              setHideAll((hidden) => !hidden);
            }}
            aria-pressed={hideAll}
            aria-label={hideAll ? t('chatView.showMessages') : t('chatView.hideMessages')}
            title={hideAll ? t('chatView.showMessages') : t('chatView.hideMessages')}
            data-testid="hide-all"
          >
            {hideAll ? <EyeIcon size={16} /> : <EyeOffIcon size={16} />}
          </button>
          {isOwner && share ? (
              <SharePopover
              buttonClassName={styles.headButton ?? ''}
              title={purpose === 'receive' ? t('chatView.shareChannel') : t('chatView.shareChat')}
              blocked={view.views >= view.maxViews ? CHAT_JOINED : null}
            >
                {share}
              </SharePopover>
          ) : null}
          {isOwner ? (
            <button type="button" className={[styles.headButton, styles.headDanger].join(' ')} onClick={onDelete} aria-label={t('chatView.deleteBridge')} title={t('chatView.deleteBridge')} data-testid="delete-bridge">
              <TrashIcon size={16} />
            </button>
          ) : null}
        </div>
      </header>

      {showCopied ? <ChatCopiedNotice onClose={() => { setClosed(true); }} /> : null}

      {notice === null ? null : (
        <p className={styles.offlineNote} role="alert" data-testid="order-notice">
          {notice}
        </p>
      )}
      {!online ? <p className={styles.offlineNote}>{t('chatView.offline')}</p> : null}

      <div ref={list} className={styles.list} role="log" aria-live="polite" aria-label={t('chatView.messages')}>
        {messageCount === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon} aria-hidden="true">
              <LockIcon size={20} />
            </span>
            <p className={styles.emptyTitle}>{t('chatView.emptyTitle')}</p>
            <p className={styles.emptyText}>{isOwner && purpose === 'receive' ? t('chatView.emptyReceive') : t('chatView.emptyChat')}</p>
            {waiting ? (
              <p className={styles.waiting} role="status" data-testid="chat-waiting">
                <span className={styles.waitDot} aria-hidden="true" data-testid="chat-waiting-dot" />
                {t('chatView.waiting')}
              </p>
            ) : null}
          </div>
        ) : null}
        {view.messages.map((message, index) => (
          <ChatMessage
            bridgeId={view.id}
            key={message.id}
            message={message}
            bridgeKey={bridgeKey}
            fetchChunk={actions.fetchChunk}
            side={sideOf(message.from, view.role, purpose)}
            avatarSeed={`${view.id}:${message.from}`}
            grouped={groupedWith(view.messages[index - 1], message)}
            canDelete={isOwner}
            onDelete={onRemoveMessage}
            online={message.from === view.role || presence === null ? undefined : presence[message.from].online}
            hidden={hideAll}
          />
        ))}
      </div>

      <form
        className={styles.composer}
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        {file ? (
          <div className={styles.attachment} data-testid="file-chip">
            <span className={styles.attachmentIcon} aria-hidden="true">
              <FileIcon size={18} />
            </span>
            <span className={styles.attachmentText}>
              <span className={styles.attachmentName}>{file.name}</span>
              <span className={styles.attachmentMeta}>{progress === null ? formatBytes(file.size) : t('chatView.sending', { percent: percent(progress) })}</span>
            </span>
            {progress === null ? (
              <button
                type="button"
                className={styles.tool}
                aria-label={t('chatView.removeFile')}
                onClick={() => {
                  setFile(null);
                }}
                disabled={sending}
              >
                <CloseIcon size={15} />
              </button>
            ) : null}
            {progress === null ? null : (
              <span className={styles.attachmentBar} role="progressbar" aria-label={t('chatView.sendingFile')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.floor(progress * 100)}>
                <span style={{ width: percent(progress) }} />
              </span>
            )}
          </div>
        ) : null}
        <label htmlFor="composer" className="visually-hidden">
          {t('chatView.messageLabel')}
        </label>
        <textarea
          ref={field}
          id="composer"
          className={styles.field}
          rows={1}
          placeholder={placeholder}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-testid="composer"
        />
        <div className={styles.bar}>
          <button type="button" className={styles.tool} aria-label={t('chatView.attach')} title={t('chatView.attach')} onClick={() => fileInput.current?.click()} disabled={sending}>
            <PaperclipIcon size={18} />
          </button>
          <input ref={fileInput} type="file" className="visually-hidden" onChange={pickFile} tabIndex={-1} aria-label={t('chatView.chooseFile')} data-testid="chat-file-input" />
          <span className={styles.hint} aria-hidden="true">
            {tr('chatView.composerHint', { b: (children) => <b>{children}</b> })}
          </span>
          <button type="submit" className={styles.send} aria-label={t('common.send')} title={t('common.send')} disabled={!canSend} data-testid="send">
            {sending ? <span className={styles.sendSpinner} /> : <ArrowUpIcon size={18} strokeWidth={2.2} />}
          </button>
        </div>
      </form>
      {sendError ? <ErrorNote notice={sendError} /> : null}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
